import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProject } from '../utils/api';

const WELCOME = "Hi! I'm your project planning assistant. Tell me what you're working on — the more detail the better — and I'll help you build a complete plan with product recommendations, a shopping list, and step-by-step instructions.";

const fmtMoney = (n) => n != null ? `$${Number(n).toFixed(2)}` : null;

const CACHE_KEY = 'intake-chat';

const loadCache = () => {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || null; } catch { return null; }
};

const ProjectIntake = () => {
  const navigate = useNavigate();
  const cached = loadCache();
  const [messages, setMessages] = useState(cached?.messages ?? [{ role: 'assistant', content: WELCOME }]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [done, setDone] = useState(cached?.done ?? false);
  const [project, setProject] = useState(cached?.project ?? null);
  const [creating, setCreating] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ messages, done, project }));
  }, [messages, done, project]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  useEffect(() => {
    if (!thinking) inputRef.current?.focus();
  }, [thinking]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setThinking(true);

    const apiMessages = newMessages.filter((m, i) => !(i === 0 && m.role === 'assistant'));

    // Placeholder message that will be filled by the stream
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const response = await fetch('/api/intake/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: apiMessages }),
      });

      if (!response.ok) {
        let msg = 'Request failed';
        try { msg = (await response.json()).message || msg; } catch { /* non-JSON */ }
        throw new Error(msg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedText = '';

      while (true) {
        const { done: streamDone, value } = await reader.read();
        if (streamDone) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(part.slice(6)); } catch { continue; }

          if (evt.type === 'text') {
            streamedText += evt.text;
            const jsonIdx = streamedText.indexOf('```json');
            const displayText = jsonIdx >= 0
              ? (streamedText.slice(0, jsonIdx).trimEnd() || '') + '\n\n_Generating project plan…_'
              : streamedText;
            setMessages((prev) => [
              ...prev.slice(0, -1),
              { role: 'assistant', content: displayText.trim() },
            ]);
          } else if (evt.type === 'done') {
            setThinking(false);
            if (evt.done && evt.project) {
              setDone(true);
              setProject(evt.project);
              setMessages((prev) => [
                ...prev.slice(0, -1),
                { role: 'assistant', content: evt.reply || streamedText },
              ]);
            }
          } else if (evt.type === 'error') {
            throw new Error(evt.message);
          }
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: `Sorry, ${err.message || 'something went wrong'}. Please try again.` },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handleCreate = async () => {
    if (!project) return;
    setCreating(true);
    try {
      const { data } = await createProject(project);
      sessionStorage.removeItem(CACHE_KEY);
      navigate(`/projects/${data._id}`);
    } finally {
      setCreating(false);
    }
  };

  const materialTotal = project?.materials?.reduce((s, m) => s + (m.estimatedCost || 0), 0) ?? 0;
  const toolsNeeded = project?.tools?.filter((t) => !t.owned) ?? [];
  const toolsTotal = toolsNeeded.reduce((s, t) => s + (t.estimatedCost || 0), 0);
  const grandTotal = materialTotal + toolsTotal;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>AI Project Planner</h1>
          <p className="page-subtitle">Describe your project — I'll scope it out and build a complete plan</p>
        </div>
      </div>

      <div className="intake-layout">
        {/* Chat */}
        <div className="intake-chat">
          <div className="intake-messages">
            {messages.map((m, i) => (
              <div key={i} className={`intake-msg intake-msg-${m.role}`}>
                {m.role === 'assistant' && <span className="intake-avatar">🤖</span>}
                <div className="intake-bubble">{m.content}</div>
              </div>
            ))}
            {thinking && messages[messages.length - 1]?.content === '' && (
              <div className="intake-msg intake-msg-assistant">
                <span className="intake-avatar">🤖</span>
                <div className="intake-bubble intake-thinking"><span /><span /><span /></div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {!done && (
            <div className="intake-input-row">
              <textarea
                ref={inputRef}
                className="intake-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe your project… (Enter to send, Shift+Enter for new line)"
                rows={2}
                disabled={thinking}
              />
              <button className="btn btn-primary intake-send" onClick={send} disabled={!input.trim() || thinking}>
                Send
              </button>
            </div>
          )}
        </div>

        {/* Project plan summary */}
        {done && project && (
          <div className="intake-summary">
            <div className="intake-summary-title">
              <h2>{project.title}</h2>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                <span className={`badge badge-priority-${project.priority}`}>{project.priority}</span>
                <span className="badge">{project.category}</span>
                {project.estimatedDuration && <span className="badge">{project.estimatedDuration}</span>}
              </div>
            </div>

            {project.description && (
              <p className="intake-summary-desc">{project.description}</p>
            )}

            {/* Materials */}
            {project.materials?.length > 0 && (
              <div className="intake-plan-section">
                <h3>🛒 Shopping List</h3>
                <table className="intake-plan-table">
                  <thead>
                    <tr><th>Item</th><th>Qty</th><th>Est. Cost</th></tr>
                  </thead>
                  <tbody>
                    {project.materials.map((m, i) => (
                      <tr key={i}>
                        <td>
                          <span className="intake-item-name">{m.name}</span>
                          {m.notes && <span className="intake-item-note">{m.notes}</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{m.quantity || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtMoney(m.estimatedCost) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  {materialTotal > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>Materials subtotal</td>
                        <td style={{ fontWeight: 700 }}>{fmtMoney(materialTotal)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {/* Tools */}
            {project.tools?.length > 0 && (
              <div className="intake-plan-section">
                <h3>🔧 Tools</h3>
                <ul className="intake-tools-list">
                  {project.tools.map((t, i) => (
                    <li key={i} className={`intake-tool-item${t.owned ? ' owned' : ' needed'}`}>
                      <span className="intake-tool-status">{t.owned ? '✓' : '+'}</span>
                      <span className="intake-tool-name">{t.name}</span>
                      {!t.owned && t.estimatedCost != null && (
                        <span className="intake-tool-cost">{fmtMoney(t.estimatedCost)}</span>
                      )}
                      {t.owned && <span className="intake-tool-own-label">you have this</span>}
                    </li>
                  ))}
                </ul>
                {toolsTotal > 0 && (
                  <p className="intake-subtotal">Tools to buy: {fmtMoney(toolsTotal)}</p>
                )}
              </div>
            )}

            {/* Cost total */}
            {grandTotal > 0 && (
              <div className="intake-cost-total">
                <span>Estimated Total</span>
                <span>{fmtMoney(grandTotal)}</span>
              </div>
            )}

            {/* Steps */}
            {project.steps?.length > 0 && (
              <div className="intake-plan-section">
                <h3>📋 Steps</h3>
                <ol className="intake-steps-list">
                  {project.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            )}

            {/* Diagram */}
            {project.diagram && (
              <div className="intake-plan-section">
                <h3>📐 Layout</h3>
                <pre className="intake-diagram">{project.diagram}</pre>
              </div>
            )}

            {/* Notes */}
            {project.notes && (
              <div className="intake-plan-section">
                <h3>⚠ Notes</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>{project.notes}</p>
              </div>
            )}

            <div className="intake-summary-actions">
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Saving…' : '✓ Save Project'}
              </button>
              <button className="btn btn-secondary" onClick={() => { sessionStorage.removeItem(CACHE_KEY); navigate('/projects'); }}>Discard</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectIntake;
